// Encrypted local mirror of the leads database.
//
// The leads API serves ~15k–61k companies across many paginated region files;
// fetching them all on every visit is slow and flaky (a single failing region
// truncates the result set). Instead we sync the whole database once into the
// browser's IndexedDB, AES-GCM encrypted at rest, and read/filter locally.
//
// Encryption: a non-extractable AES-GCM CryptoKey is generated on first sync and
// kept in the `meta` store. Because it's non-extractable, the raw key bytes can
// never be read back out — every region blob in IndexedDB is ciphertext, so the
// company data isn't sitting in the clear in DevTools.

import { useCallback, useEffect, useState } from 'react'
import { useManifest, loadRegionRows, annotateRows } from './data.jsx'
import { hubspotIndex } from './hubspot.js'
import { crmLeadRows, claimedCompanyIds } from './crmLeads.js'

const DB_NAME = 'revailosales-leads'
const DB_VERSION = 1
const STORE_REGIONS = 'regions' // key `${category}/${slug}` → { iv, ct, count, syncedAt }
const STORE_META = 'meta' // key string → value (incl. the CryptoKey and per-category sync state)
const KEY_ID = 'cryptoKey'

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_REGIONS)) db.createObjectStore(STORE_REGIONS)
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbGet(db, store, key) {
  return new Promise((resolve, reject) => {
    const req = db.transaction(store, 'readonly').objectStore(store).get(key)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbPut(db, store, key, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite')
    tx.objectStore(store).put(value, key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

function idbDelete(db, store, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite')
    tx.objectStore(store).delete(key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

function idbGetAll(db, store) {
  return new Promise((resolve, reject) => {
    const os = db.transaction(store, 'readonly').objectStore(store)
    const keysReq = os.getAllKeys()
    const valsReq = os.getAll()
    let keys, vals
    keysReq.onsuccess = () => {
      keys = keysReq.result
      if (vals) resolve(keys.map((k, i) => [k, vals[i]]))
    }
    valsReq.onsuccess = () => {
      vals = valsReq.result
      if (keys) resolve(keys.map((k, i) => [k, vals[i]]))
    }
    keysReq.onerror = valsReq.onerror = () => reject(keysReq.error || valsReq.error)
  })
}

async function getKey(db) {
  let key = await idbGet(db, STORE_META, KEY_ID)
  if (!key) {
    key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ])
    await idbPut(db, STORE_META, KEY_ID, key)
  }
  return key
}

async function encryptJSON(key, obj) {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const data = new TextEncoder().encode(JSON.stringify(obj))
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data)
  return { iv, ct }
}

async function decryptJSON(key, iv, ct) {
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct)
  return JSON.parse(new TextDecoder().decode(pt))
}

const metaKey = (category) => `sync:${category}`

// Bumped after every completed sync so readers that memoise the mirror for
// the session (lib/appSearch.js) know to re-read it instead of scanning the
// rows they loaded before the sync.
let mirrorGeneration = 0
export const getMirrorGeneration = () => mirrorGeneration

// Compact identity of a category's server-side dataset: every region slug with
// its row count. The manifest is fetched fresh on each app load, so when the
// backend data is reloaded (e.g. a re-scrape imported into DynamoDB) the
// fingerprint changes and the local mirror knows it's outdated.
//
// An imported region also carries `updated` (lib/leadImport.js, 2026-10-20):
// it moves when a teammate re-imports the region AND when an enrichment writes
// its findings back onto an imported row - neither changes the count, so
// without it the mirror would keep showing those rows as they were imported.
// Loaded regions have no stamp, so their part of the string is unchanged and
// nobody's mirror re-syncs because this line shipped.
export function manifestFingerprint(manifest, category) {
  const regions = manifest?.categories?.[category] ?? []
  return regions
    .map((r) => `${r.slug}:${r.count || 0}${r.updated ? `@${r.updated}` : ''}`)
    .sort()
    .join('|')
}

// Everything cached for a category: decrypt each region blob, tag + dedupe rows.
// ── one row per COMPANY, not per region worksheet ───────────────────────────
//
// The import is per region: a contractor licensed across five Texas regions
// arrives as five rows, and the loader hashes the region into the id, so every
// copy carries a DIFFERENT company_id. The dedupe above keys on exactly that
// field, so it never fired — the directory listed Texas Lath & Masonry five
// times, "Replied to us" repeated it five times, and one outreach run could
// draft five separate emails to the same inbox. Identity has to be the
// BUSINESS, not the row.
//
// Identity = normalized name + the strongest contact key the row carries
// (phone, then email, then website). A row with a name and NO contact key of
// any kind is left alone: two different "ABC Masonry" with nothing else in
// common must never be welded into one.
//
// The survivor is deterministic (lowest company_id in the group) so the same
// row wins on every reload — lists, notes and dossiers key off it. Its empty
// fields are then filled from its twins, which is why the merged row is
// strictly richer than any single copy: one twin knew the revenue band, another
// only said "Unknown". Nothing real is ever overwritten.
const NOTHING = /^(unknown|n\/a|none|not provided|-|—)?$/i
const hasValue = (v) => typeof v === 'string' && v.trim() && !NOTHING.test(v.trim())
const normName = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
const digitsOf = (v) => String(v || '').replace(/\D/g, '')

function identityOf(row) {
  const name = normName(row.name)
  if (!name) return ''
  const phone = digitsOf(row.phone || row.contact_phone)
  const email = String(row.email || '').trim().toLowerCase()
  const site = String(row.website || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
  const contact = phone || email || site
  // No contact key at all: keep this row on its own identity.
  return contact ? `${name}|${contact}` : ''
}

export function mergeDuplicateCompanies(rows) {
  const groups = new Map()
  const order = []
  for (const row of rows) {
    const id = identityOf(row)
    if (!id) {
      order.push({ single: row })
      continue
    }
    if (!groups.has(id)) {
      const slot = { list: [] }
      groups.set(id, slot)
      order.push(slot)
    }
    groups.get(id).list.push(row)
  }
  const out = []
  for (const slot of order) {
    if (slot.single) {
      out.push(slot.single)
      continue
    }
    const list = slot.list
    if (list.length === 1) {
      out.push(list[0])
      continue
    }
    const sorted = [...list].sort((a, b) =>
      String(a.company_id || a.profile_link || a.name).localeCompare(
        String(b.company_id || b.profile_link || b.name),
      ),
    )
    const base = { ...sorted[0] }
    const areas = []
    const regions = []
    const dupKeys = []
    for (const twin of sorted) {
      for (const area of String(twin.service_area || '').split(/\s*[·;]\s*/)) {
        if (hasValue(area) && !areas.includes(area.trim())) areas.push(area.trim())
      }
      const reg = String(twin.region || '').trim()
      if (reg && !regions.includes(reg)) regions.push(reg)
      if (twin !== sorted[0]) {
        const k = String(twin.company_id || twin.profile_link || twin.name || '')
        if (k) dupKeys.push(k)
        // Fill only what the survivor genuinely lacks.
        for (const [field, value] of Object.entries(twin)) {
          if (field.startsWith('_')) continue
          if (!hasValue(base[field]) && hasValue(value)) base[field] = value
        }
      }
    }
    // The company's real coverage, bounded so a five-region contractor does not
    // push a paragraph into a table cell (or into a draft's "You cover …").
    if (areas.length) {
      base.service_area = areas.length > 3 ? `${areas.slice(0, 3).join(', ')} +${areas.length - 3} more` : areas.join(', ')
    }
    base._regions = regions
    base._dupKeys = dupKeys
    base._dupCount = list.length
    out.push(base)
  }
  return out
}

export async function getCachedCompanies(category) {
  const db = await openDB()
  const key = await getKey(db)
  const entries = await idbGetAll(db, STORE_REGIONS)
  const prefix = `${category}/`
  const seen = new Set()
  const rows = []
  let decryptFailures = 0
  for (const [k, rec] of entries) {
    if (!k.startsWith(prefix) || !rec) continue
    let regionRows
    try {
      regionRows = await decryptJSON(key, rec.iv, rec.ct)
    } catch {
      decryptFailures += 1 // key rotated / corrupt blob — this region is unreadable
      continue
    }
    for (const row of regionRows) {
      const id = row.company_id || row.profile_link || row.name
      if (seen.has(id)) continue
      seen.add(id)
      rows.push(row)
    }
  }
  const merged = mergeDuplicateCompanies(rows)
  const meta = (await idbGet(db, STORE_META, metaKey(category))) || null
  // If any blob couldn't be decrypted, the cache is a mix of old/new-key regions
  // — surface it as stale so the caller does a clean full re-sync instead of
  // showing a misleading partial subset.
  return { rows: merged, meta, stale: decryptFailures > 0 }
}

export async function getSyncMeta(category) {
  const db = await openDB()
  return (await idbGet(db, STORE_META, metaKey(category))) || null
}

// Fetch every region for a category and store each as an encrypted blob. A
// region that fails (API hiccup) is retried once, then recorded as failed but
// doesn't abort the rest — so partial syncs accumulate and can be topped up.
// `force: true` (manual Sync, or a changed manifest fingerprint) makes the
// server authoritative: fetched regions overwrite the cache even when they
// shrank, and cached regions the manifest no longer lists are dropped.
export async function syncCategory(category, manifest, mode, onProgress, { force = false } = {}) {
  const db = await openDB()
  const key = await getKey(db)
  const regions = manifest?.categories?.[category] ?? []
  const total = regions.length
  let done = 0
  let count = 0
  const failed = []

  // Regions are fetched through a small worker pool instead of one-by-one —
  // sync time is dominated by request round-trips, and ~6 in flight matches
  // the browser's per-origin connection budget. Progress ticks as each region
  // completes, in whatever order they finish.
  const CONCURRENCY = 6
  let nextIndex = 0
  async function syncOneRegion(region) {
    let rows = null
    for (let attempt = 0; attempt < 2 && !rows; attempt++) {
      try {
        rows = await loadRegionRows(mode, category, region, { fresh: true })
      } catch {
        rows = null
      }
    }
    const cacheKey = `${category}/${region.slug}`
    const existing = await idbGet(db, STORE_REGIONS, cacheKey)
    const existingCount = existing?.count || 0
    // Outside force mode, only (over)write when the fetch returned at least as
    // many rows as the cached copy — a flaky/empty re-fetch must never shrink a
    // good region. A failed fetch (rows = null) never clobbers either way.
    if (rows && (force || rows.length >= existingCount)) {
      const { iv, ct } = await encryptJSON(key, rows)
      await idbPut(db, STORE_REGIONS, cacheKey, {
        iv,
        ct,
        count: rows.length,
        syncedAt: Date.now(),
      })
      count += rows.length
    } else {
      count += existingCount
      if (!existing) failed.push(region.slug)
    }
    done += 1
    onProgress?.({ done, total, count, label: region.label })
  }
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, regions.length) }, async () => {
      while (nextIndex < regions.length) {
        const region = regions[nextIndex++]
        await syncOneRegion(region)
      }
    }),
  )

  // Drop cached regions that no longer exist server-side (renamed/removed
  // slugs after a data reload), then recount from what's actually stored
  // (covers earlier partial syncs).
  const prefix = `${category}/`
  const valid = new Set(regions.map((r) => `${prefix}${r.slug}`))
  const entries = await idbGetAll(db, STORE_REGIONS)
  for (const [k] of entries) {
    if (k.startsWith(prefix) && !valid.has(k)) await idbDelete(db, STORE_REGIONS, k)
  }
  const syncedRegions = entries.filter(([k, v]) => valid.has(k) && v).map(([k]) => k)
  const meta = {
    lastSynced: Date.now(),
    syncedRegions: syncedRegions.length,
    totalRegions: total,
    failed,
    fingerprint: manifestFingerprint(manifest, category),
  }
  await idbPut(db, STORE_META, metaKey(category), meta)
  mirrorGeneration += 1
  return meta
}

// ---------- React hook ----------

export function useLeadsCache(category) {
  const { manifest, mode } = useManifest()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [meta, setMeta] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const [progress, setProgress] = useState(null)

  // Wire the HubSpot mirror onto the directory's rows. This is the loader the
  // directory ACTUALLY uses (useCategoryCompanies in data.jsx is a different
  // path), so the merge has to happen here or the Source column, the HubSpot
  // badge and the Owner/Deals/Activity columns never see a thing.
  //
  // annotateRows is purely additive — it only sets _source / _hubspot and never
  // touches a row's own imported fields, so every enrichment column (score,
  // sales_angles, objections, estimators, …) is carried through untouched.
  // Paint immediately (so _source is set on the first frame, hardcoded to
  // 'hubspot'), then upgrade in place once the one-per-session index resolves
  // and real matches can populate owner/deals/activity. Fails soft: a null
  // index leaves every row HubSpot-sourced with empty match data.
  const applyRows = useCallback((next) => {
    annotateRows(next, null)
    setRows(next)
    hubspotIndex()
      .then((index) => {
        if (!index) return
        annotateRows(next, index)
        // EVERY MIRRORED COMPANY THE IMPORT NEVER HAD, listed too. The CRM is
        // the book of business; this file is one slice of it. A company Bo is
        // working that was never imported used to be invisible here — no row,
        // no outreach state, nothing. Now it is a lead like any other, marked
        // `_crmOnly` and carrying only what the mirror actually holds.
        const crm = crmLeadRows(index, claimedCompanyIds(next))
        setRows(crm.length ? [...next, ...crm] : [...next])
      })
      .catch(() => {
        /* annotation is best-effort; the directory already rendered */
      })
  }, [])

  const reload = useCallback(async () => {
    const { rows, meta, stale } = await getCachedCompanies(category)
    applyRows(stale ? [] : rows)
    setMeta(meta)
    return { meta, stale }
  }, [category, applyRows])

  const sync = useCallback(
    async (force = false) => {
      if (!manifest) return
      setSyncing(true)
      setProgress({ done: 0, total: manifest.categories?.[category]?.length ?? 0, count: 0 })
      try {
        await syncCategory(category, manifest, mode, (p) => setProgress(p), {
          force: force === true,
        })
        await reload()
      } finally {
        setSyncing(false)
        setProgress(null)
      }
    },
    [manifest, mode, category, reload],
  )

  // Load from cache on mount; auto-sync the first time (empty cache).
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getCachedCompanies(category)
      .then(({ rows, meta, stale }) => {
        if (cancelled) return
        // On a stale cache (undecryptable blobs from a rotated key), don't show
        // the readable-subset — blank it and re-sync cleanly so the count never
        // flashes a partial number.
        applyRows(stale ? [] : rows)
        setMeta(meta)
        setLoading(false)
        // Auto-sync when the cache is empty, stale, or only partially
        // populated — or when the server dataset changed since this mirror was
        // synced (manifest fingerprint mismatch, e.g. after a data reload).
        // Outdated caches force-sync so the server truth overwrites them.
        const partial = meta && meta.totalRegions && meta.syncedRegions < meta.totalRegions
        const outdated =
          Boolean(meta?.lastSynced) &&
          manifest &&
          meta.fingerprint !== manifestFingerprint(manifest, category)
        if ((!meta?.lastSynced || partial || stale || outdated) && manifest) {
          sync(Boolean(outdated) || stale)
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, manifest])

  return { rows, loading, meta, syncing, progress, sync }
}
